import type Highcharts from 'highcharts/highstock';
import type { Translator } from 'i18n/i18n-context';

type Tokens = { [token: string]: string };
type Plain = { [key: string]: unknown };

// app `:token` placeholders mapped to the Highcharts template expressions they stand for
const SERIES_TOKENS: Tokens = {
    name: '{series.name}',
    index: '{seriesNumber}',
    total: '{chart.series.length}',
    count: '{series.points.length}',
};
const POINTS_TOKENS: Tokens = { count: '{numPoints}' };
const SERIES_COUNT_TOKENS: Tokens = { count: '{numSeries}' };
const AXIS_ONE_TOKENS: Tokens = { name: '{names[0]}', range: '{ranges[0]}' };
const AXIS_OTHER_TOKENS: Tokens = {
    count: '{numAxes}',
    names: '{#each names}{#unless @first}, {/unless}{this}{/each}',
};

/** Canonical BCP 47 tag for Intl and Highcharts, falling back to English for anything unrecognised. */
const canonicalLocale = (locale: string | undefined): string => {
    try {
        return new Intl.Locale(locale || 'en').toString();
    } catch {
        return 'en';
    }
};

const dateNames = (
    locale: string,
    count: number,
    toTime: (i: number) => number,
    options: Intl.DateTimeFormatOptions,
) => {
    const format = new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' });
    return Array.from({ length: count }, (_, i) => format.format(toTime(i)));
};

// English separators, as before `lang.locale` was set, so chart numbers match tables until numbers are localised app-wide
const decimalPoint = '.';
const thousandsSep = ',';

/** Drops undefined leaves so Highcharts keeps its English default for any key not translated yet. */
const compact = <T extends Plain>(value: T): T =>
    Object.fromEntries(
        Object.entries(value).flatMap(([key, entry]) => {
            if (entry === undefined) {
                return [];
            }
            if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
                const inner = compact(entry as Plain);
                return Object.keys(inner).length ? [[key, inner]] : [];
            }
            return [[key, entry]];
        }),
    ) as T;

/** Builds Highcharts `lang` options from app translations and Intl date names for the given locale. */
export const buildHighchartsLang = (__: Translator, locale: string | undefined): Highcharts.LangOptions => {
    const tag = canonicalLocale(locale);

    const t = (key: string, tokens?: Tokens) => {
        const value = __(key, tokens);
        return value && value !== key ? value : undefined;
    };
    const plural = (key: string, count: string, tokens: Tokens) => {
        const one = t(`${key}_one`, tokens);
        const other = t(`${key}_other`, tokens);
        return one && other ? `{#eq ${count} 1}${one}{else}${other}{/eq}` : undefined;
    };
    const seriesSummary = (key: string) => plural(key, 'series.points.length', SERIES_TOKENS);

    const navigatorStart = t('highcharts.navigator_start_handle');
    const navigatorEnd = t('highcharts.navigator_end_handle');
    const annotationRelated = t('highcharts.annotation_related_to', {
        text: '{annotationText}',
        point: '{annotationPoint}',
    });
    const annotationAlso = t('highcharts.annotation_also_related_to', { point: '{this}' });

    const summary = seriesSummary('highcharts.series_summary');
    const lineSummary = seriesSummary('highcharts.series_summary_line');
    const lineCombination = seriesSummary('highcharts.series_summary_line_combination');
    const barSummary = seriesSummary('highcharts.series_summary_bar');
    const barCombination = seriesSummary('highcharts.series_summary_bar_combination');

    const defaultSingle = plural('highcharts.chart_type_default', 'numPoints', POINTS_TOKENS);
    const lineSingle = plural('highcharts.chart_type_line', 'numPoints', POINTS_TOKENS);
    const lineMultiple = t('highcharts.chart_type_line_multiple', SERIES_COUNT_TOKENS);
    const barSingle = plural('highcharts.chart_type_bar', 'numPoints', POINTS_TOKENS);
    const barMultiple = t('highcharts.chart_type_bar_multiple', SERIES_COUNT_TOKENS);
    const resetZoom = t('misc.reset_zoom');
    const range = { range: '{range}' };

    return compact({
        locale: tag,
        months: dateNames(tag, 12, (month) => Date.UTC(2000, month, 1), { month: 'long' }),
        shortMonths: dateNames(tag, 12, (month) => Date.UTC(2000, month, 1), { month: 'short' }),
        // 2000-01-02 was a Sunday, where Highcharts' weekday arrays start
        weekdays: dateNames(tag, 7, (day) => Date.UTC(2000, 0, 2 + day), { weekday: 'long' }),
        shortWeekdays: dateNames(tag, 7, (day) => Date.UTC(2000, 0, 2 + day), { weekday: 'short' }),
        decimalPoint,
        thousandsSep,
        loading: t('misc.loading'),
        resetZoom,
        resetZoomTitle: t('highcharts.reset_zoom_title'),
        rangeSelectorZoom: t('misc.zoom'),
        viewData: t('highcharts.view_data'),
        hideData: t('highcharts.hide_data'),
        contextButtonTitle: t('highcharts.context_button_title'),
        yAxisTitle: t('highcharts.y_axis_title'),
        seriesName: t('highcharts.series_name', { number: '{add index 1}' }),
        pieSliceName: t('highcharts.pie_slice_name'),
        weekFrom: t('highcharts.week_from'),
        exportData: {
            categoryHeader: t('highcharts.table_category_header'),
            categoryDatetimeHeader: t('highcharts.table_datetime_header'),
        },
        accessibility: {
            thousandsSep,
            defaultChartTitle: t('misc.chart'),
            chartContainerLabel: t('highcharts.chart_container_label', { title: '{title}' }),
            svgContainerLabel: t('highcharts.svg_container_label'),
            screenReaderSection: {
                endOfChartMarker: t('highcharts.end_of_chart'),
                annotations: {
                    heading: t('highcharts.annotations_heading'),
                    descriptionSinglePoint: annotationRelated,
                    descriptionMultiplePoints:
                        annotationRelated && annotationAlso
                            ? `${annotationRelated}{#each additionalAnnotationPoints}, ${annotationAlso}{/each}`
                            : undefined,
                },
            },
            legend: {
                legendItem: t('highcharts.legend_item', { name: '{itemName}' }),
                legendLabel: t('highcharts.legend_label', { title: '{legendTitle}' }),
                legendLabelNoTitle: t('highcharts.legend_label_no_title', { title: '{chartTitle}' }),
            },
            zoom: {
                mapZoomIn: t('highcharts.zoom_in'),
                mapZoomOut: t('highcharts.zoom_out'),
                resetZoomButton: resetZoom,
            },
            rangeSelector: {
                minInputLabel: t('highcharts.range_min_input'),
                maxInputLabel: t('highcharts.range_max_input'),
                clickButtonAnnouncement: t('highcharts.range_viewing', { range: '{axisRangeDescription}' }),
            },
            navigator: {
                handleLabel:
                    navigatorStart && navigatorEnd
                        ? `{#eq handleIx 0}${navigatorStart}{else}${navigatorEnd}{/eq}`
                        : undefined,
                groupLabel: t('highcharts.navigator_group'),
            },
            table: {
                viewAsDataTableButtonText: t('highcharts.view_as_data_table', { title: '{chartTitle}' }),
                tableSummary: t('highcharts.table_summary'),
            },
            exporting: {
                chartMenuLabel: t('highcharts.chart_menu_label'),
                menuButtonLabel: t('highcharts.chart_menu_button', { title: '{chartTitle}' }),
            },
            seriesTypeDescriptions: {
                boxplot: t('highcharts.series_type_boxplot'),
                arearange: t('highcharts.series_type_arearange'),
            },
            chartTypes: {
                emptyChart: t('highcharts.chart_type_empty'),
                combinationChart: t('highcharts.chart_type_combination', SERIES_COUNT_TOKENS),
                defaultSingle,
                defaultMultiple: t('highcharts.chart_type_default_multiple', SERIES_COUNT_TOKENS),
                lineSingle,
                lineMultiple,
                splineSingle: lineSingle,
                splineMultiple: lineMultiple,
                columnSingle: barSingle,
                columnMultiple: barMultiple,
                barSingle,
                barMultiple,
                boxplotSingle: plural('highcharts.chart_type_boxplot', 'numPoints', POINTS_TOKENS),
                boxplotMultiple: t('highcharts.chart_type_boxplot_multiple', SERIES_COUNT_TOKENS),
                pieSingle: plural('highcharts.chart_type_pie', 'numPoints', POINTS_TOKENS),
                pieMultiple: t('highcharts.chart_type_pie_multiple', SERIES_COUNT_TOKENS),
            },
            axis: {
                xAxisDescriptionSingular: t('highcharts.axis_x_one', AXIS_ONE_TOKENS),
                xAxisDescriptionPlural: t('highcharts.axis_x_other', AXIS_OTHER_TOKENS),
                yAxisDescriptionSingular: t('highcharts.axis_y_one', AXIS_ONE_TOKENS),
                yAxisDescriptionPlural: t('highcharts.axis_y_other', AXIS_OTHER_TOKENS),
                timeRangeDays: t('highcharts.axis_range_days', range),
                timeRangeHours: t('highcharts.axis_range_hours', range),
                timeRangeMinutes: t('highcharts.axis_range_minutes', range),
                timeRangeSeconds: t('highcharts.axis_range_seconds', range),
                rangeFromTo: t('highcharts.axis_range_from_to', { from: '{rangeFrom}', to: '{rangeTo}' }),
                rangeCategories: t('highcharts.axis_range_categories', { count: '{numCategories}' }),
                defaultAxisNames: {
                    categories: t('highcharts.axis_name_categories'),
                    time: t('misc.time'),
                    values: t('highcharts.axis_name_values'),
                },
            },
            series: {
                summary: {
                    default: summary,
                    defaultCombination: summary,
                    line: lineSummary,
                    lineCombination,
                    spline: lineSummary,
                    splineCombination: lineCombination,
                    column: barSummary,
                    columnCombination: barCombination,
                    bar: barSummary,
                    barCombination,
                    boxplot: seriesSummary('highcharts.series_summary_boxplot'),
                    boxplotCombination: seriesSummary('highcharts.series_summary_boxplot_combination'),
                    pie: seriesSummary('highcharts.series_summary_pie'),
                    pieCombination: summary,
                },
                xAxisDescription: t('highcharts.x_axis_label', { name: '{name}' }),
                yAxisDescription: t('highcharts.y_axis_label', { name: '{name}' }),
                nullPointValue: t('highcharts.no_value'),
            },
        },
    } as Highcharts.LangOptions & Plain);
};

/** Sets the global lang and pushes it into open charts, which copy lang at creation. */
export const applyHighchartsLang = (instance: typeof Highcharts, lang: Highcharts.LangOptions): void => {
    instance.setOptions({ lang });

    instance.charts.forEach((chart) => {
        if (!chart?.options.lang) {
            return;
        }
        // chart-level lang (e.g. chartAccessibility labels) still wins over the global strings
        instance.merge(true, chart.options.lang, lang, chart.userOptions.lang);
        // same flag the accessibility module sets on chart.update, so the next render rebuilds its text
        (chart as Highcharts.Chart & { a11yDirty?: boolean }).a11yDirty = true;
        chart.redraw(false);
    });
};

type LangListener = (lang: Highcharts.LangOptions) => void;

let currentLang: Highcharts.LangOptions | undefined;
const listeners = new Set<LangListener>();

/** Stores the app's Highcharts lang and hands it to the Highcharts instance if that has loaded. */
export const publishHighchartsLang = (lang: Highcharts.LangOptions): void => {
    currentLang = lang;
    listeners.forEach((listener) => listener(lang));
};

/** Calls the listener with the latest published lang now (if any) and on every later publish. */
export const subscribeHighchartsLang = (listener: LangListener): (() => void) => {
    listeners.add(listener);
    if (currentLang) {
        listener(currentLang);
    }
    return () => {
        listeners.delete(listener);
    };
};
